<script>
import baseMixin from '@mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import dayjs from '@/services/date'
import { orderBy } from 'lodash'
import { DisputeTypeStrings } from '@/constants/disputes/DisputeType'
import VuetifyColourClass from '@/constants/core/VuetifyColourClass'
import DateFormatToken from '@/constants/date/DateFormatToken'
import { ShortTermStatus } from '@/constants/booking/ShortTermStatus'
import { DisputePerspective } from '@/constants/disputes/DisputePerspective.js'
import Spinner from '@/components/general/spinner.vue'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import ClientDetailsListItem from '../general/client-details-list-item.vue'
import DisputesTableBookingDate from './disputes-table-booking-date.vue'
import DisputesTableSubmittedIndicator from './disputes-table-submitted-indicator.vue'
import DisputesTableSimpleDelta from './disputes-table-simple-delta.vue'

export default {
  components: {
    ClientDetailsListItem,
    Spinner,
    DisputesTableBookingDate,
    DisputesTableSubmittedIndicator,
    DisputesTableSimpleDelta,
  },
  mixins: [baseMixin],
  props: {
    autocompleteSelectedModel: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedTimesheets: [],
      isBulkSubmitLoading: [],
      tableHeaders: [
        {
          text: 'Booking Id',
          align: 'start',
          value: 'bookingId',
        },
        { text: 'Location Details', value: 'locationName' },
        { text: 'Candidate', value: 'candidateDetails' },
        { text: 'Booking Time', value: 'bookingDateTime' },
        { text: 'Delta', value: 'deltaMinutes' },
        { text: 'Ca', value: 'candidateHasSubmitted' },
        { text: 'Cl', value: 'clientHasSubmitted' },
        { text: '', value: 'actions', align: 'end', sortable: false },
      ],
      disputesPagedList: [],
      tableOptions: {},
      disputePerspective: DisputePerspective,
    }
  },
  computed: {
    ...mapGetters({
      isLoadingBookingDisputes: 'disputes/isLoading',
      currentUser: 'auth/currentUser',
      staffByAdminIds: 'staff/getStaffByIds',
    }),
    disputes() {
      return orderBy(
        this.disputesPagedList?.data?.map((item) => ({
          ...item,
          isSelectable: !!(
            (item?.clientTimesheet?.details?.resultingStatus
              ? ShortTermStatus.BULK_SUBMITABLE.includes(
                  item?.clientTimesheet?.details?.resultingStatus
                )
              : true) &&
            (item?.candidateTimesheet?.details?.resultingStatus
              ? ShortTermStatus.BULK_SUBMITABLE.includes(
                  item?.clientTimesheet?.details?.resultingStatus
                )
              : true)
          ),
        })) || [],
        ['deltaMinutes', 'candidateHasSubmitted', 'clientHasSubmitted'],
        ['desc', 'desc', 'desc']
      )
    },
    disputesLength() {
      return this.disputesPagedList?.totalCount || 0
    },
    displayDisputeAlert() {
      return this.disputesLength >= 1000
    },
    hasTimesheetsSelected() {
      return this.selectedTimesheets.length > 0
    },
    filters() {
      return {
        placementStatuses: [
          ...ShortTermStatus.getAllMissingTimesheets(),
          ...ShortTermStatus.getConflictingTimesheetStatuses(),
        ],
        searchTerm: null,
        fromDate: null,
        untilDate: null,
        pageNumber: 1,
        pageSize: 1000,
        adminIds: this.autocompleteSelectedModel,
        invoiceIds: [],
      }
    },
  },
  watch: {
    tableOptions: {
      async handler(val) {
        this.filters.pageSize = val.itemsPerPage
        this.filters.pageNumber = val.page
        await this.loadList()
      },
      deep: true,
    },
    filters: {
      async handler() {
        await this.loadList()
        this.$emit('setGraphData', this.disputesPagedList.data)
      },
      deep: true,
    },
  },
  async created() {
    if (this.autocompleteSelectedModel.length > 0) await this.loadList()
  },
  methods: {
    ...mapActions({
      getBookingDisputeList: 'disputes/getDisputes',
      loadBookingDetails: 'booking/loadDetails',
    }),
    async loadList(refresh) {
      const result = await this.getBookingDisputeList({
        params: this.filters,
        refresh,
      })
      if (result.isSuccess) {
        this.disputesPagedList = result.data
      }
    },
    formatDate(d, format = DateFormatToken.ll) {
      return dayjs(d).locale(this.$i18n.locale).format(format)
    },
    formatTimeString(timeString) {
      return dayjs(timeString, DateFormatToken.HHmm).format(DateFormatToken.LT)
    },
    status(disputeItem) {
      return {
        color: () => {
          const isMissing = ShortTermStatus.getAllMissingTimesheets().includes(
            disputeItem.bookingStatus
          )
          return isMissing ? VuetifyColourClass.warning : VuetifyColourClass.red
        },
        text: () => {
          const isMissing = ShortTermStatus.getAllMissingTimesheets().includes(
            disputeItem.bookingStatus
          )
          return isMissing
            ? DisputeTypeStrings.MISSING
            : DisputeTypeStrings.CONFLICT
        },
      }
    },
    async submitSelectedTimesheets() {
      this.isBulkSubmitLoading = this.selectedTimesheets.map(
        (item) => item.bookingId
      )

      const payload = this.selectedTimesheets.map((item) => ({
        bookingId: item.bookingId,
        operation: ResolveOperations.ORIGINAL,
      }))

      await this.$store.dispatch('disputes/submitTimesheets', payload)
      this.isBulkSubmitLoading = []
      this.selectedTimesheets = []
      await this.loadList(true)
    },
    loadBookingDetails(item) {
      this.$emit('loadBookingDetails', item)
    },
    /**
     * The table expects a function to return the item class.
     */
    getTableItemClass() {
      return 'cursor-pointer'
    },
  },
}
</script>

<template>
  <div>
    <v-alert v-if="displayDisputeAlert" type="info" text prominent dismissible>
      Only displaying the first 1000 records
    </v-alert>
    <v-data-table
      v-model="selectedTimesheets"
      :headers="tableHeaders"
      :items="disputes"
      :loading="isLoadingBookingDisputes"
      :class="['rounded-xl custom-elevation pt-2']"
      :item-class="getTableItemClass"
      :footer-props="{
        itemsPerPageOptions: [5, 10, 15, 20],
      }"
      item-key="bookingId"
      show-select
      selectable-key="isSelectable"
      :sort-desc="[
        'deltaMinutes',
        'candidateHasSubmitted',
        'clientHasSubmitted',
      ]"
      @click:row="loadBookingDetails"
    >
      <template v-slot:item.bookingId="{ item }">
        <div v-if="!isBulkSubmitLoading.includes(item.bookingId)" @click.stop>
          <BaseLink
            :href="returnBookingUrl(item.bookingId)"
            color="primary"
            :title="$t('general.link.viewBookingTooltip')"
            allow-insecure
            >{{ item.bookingId }}</BaseLink
          >
        </div>
        <Spinner v-else />
      </template>

      <template v-slot:item.locationName="{ item }">
        <ClientDetailsListItem :location-id="item.locationId" />
      </template>

      <template v-slot:item.candidateDetails="{ item }">
        <div @click.stop>
          <BaseLink
            :href="returnCandidateUrl(item.candidateId)"
            color="primary"
            :title="$t('general.link.viewCandidateTooltip')"
            allow-insecure
            >{{ item.candidateName }}</BaseLink
          >
        </div>
      </template>
      <template v-slot:item.deltaMinutes="{ item }">
        <DisputesTableSimpleDelta :dispute="item" />
      </template>

      <template v-slot:item.bookingDateTime="{ item }">
        <DisputesTableBookingDate :dispute="item" />
      </template>

      <template v-slot:item.candidateHasSubmitted="{ item }">
        <DisputesTableSubmittedIndicator
          v-if="item.candidateHasSubmitted"
          :timesheet="item.candidateTimesheet"
        />
      </template>
      <template v-slot:item.clientHasSubmitted="{ item }">
        <DisputesTableSubmittedIndicator
          v-if="item.clientHasSubmitted"
          :timesheet="item.clientTimesheet"
        />
      </template>

      <template v-slot:item.actions class="text-align-right">
        <v-icon size="20" class="opacity-7">mdi-chevron-right</v-icon>
      </template>
      <template v-slot:footer.prepend>
        <v-btn
          color="primary"
          class="ma-2 px-8"
          depressed
          :disabled="!hasTimesheetsSelected"
          :loading="isLoadingBookingDisputes"
          @click="submitSelectedTimesheets"
        >
          {{ $t('general.submit') }}
        </v-btn>
        <p class="mb-0 ml-2 text-body-1 font-weight-light">{{
          $t('dispute.table.selectedText', [selectedTimesheets.length])
        }}</p>
      </template>
    </v-data-table>
  </div>
</template>
